import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { NEW_CONVERSATION_PARAM } from '@/lib/constants/widget-events';
import {
  readJsonFromLocalStorage,
  safeLocalStorage,
  writeJsonToLocalStorage,
} from '@/lib/storage/safe-storage';
import { isLocalThreadId } from '@/lib/utils/thread';

const SYNC_STORAGE_KEY = 'sync-storage';
const SYNC_CONFIG_KEY = 'bazak-sync-config';

interface SyncConfig {
  syncTimeout: number;
}

export interface SidebarState {
  desktop: boolean;
  mobile: boolean;
}

export interface SyncState {
  sidebar: SidebarState;
  composerTexts: Record<string, string>;
  threadId: string | null;
  heartbeatTimestamp: number | null;
}

export interface SyncActions {
  setSidebar: (value: Partial<SidebarState>) => void;
  setComposerText: (threadId: string, text: string) => void;
  clearComposerText: (threadId: string) => void;
  setThreadId: (value: string | null) => void;
  setHeartbeatTimestamp: (value: number | null) => void;
}

export type SyncStore = SyncState & SyncActions;

export function setSyncConfig(config: SyncConfig): void {
  if (typeof window !== 'undefined') {
    writeJsonToLocalStorage({ key: SYNC_CONFIG_KEY, value: config });
  }
}

function getSyncTimeout(): number {
  if (typeof window === 'undefined') return 0;
  const config = readJsonFromLocalStorage<SyncConfig | null>({
    key: SYNC_CONFIG_KEY,
    fallback: null,
  });
  return config?.syncTimeout ?? 0;
}

const defaultState: SyncState = {
  sidebar: { desktop: true, mobile: false },
  composerTexts: {},
  threadId: null,
  heartbeatTimestamp: null,
};

/**
 * The persisted threadId straight from localStorage, bypassing zustand's hydration timing.
 * Restore logic uses it to tell "no thread was persisted" apart from "the store hasn't
 * delivered the hydrated value yet".
 */
export function readRawPersistedThreadId(): string | null {
  const raw = readJsonFromLocalStorage<{ state?: { threadId?: string | null } } | null>({
    key: SYNC_STORAGE_KEY,
    fallback: null,
  });
  const threadId = raw?.state?.threadId;
  return typeof threadId === 'string' && threadId.length > 0 ? threadId : null;
}

function isNewConversationRequested(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get(NEW_CONVERSATION_PARAM) === '1';
}

/** One-shot, like the force-restore flag: a reload (a locale change) must restore the thread this
 *  boot started, so the param leaves the frame's own URL once the boot has read it. */
export function clearNewConversationRequest(): void {
  if (!isNewConversationRequested()) return;
  const url = new URL(window.location.href);
  url.searchParams.delete(NEW_CONVERSATION_PARAM);
  window.history.replaceState(window.history.state, '', url);
}

/**
 * The thread id to restore at boot, applying the same gating as the persist `merge` below
 * (one-shot force-restore flag, or a heartbeat fresh within the tenant's syncTimeout) but read
 * straight from localStorage: the runtime needs it synchronously at first render (as
 * `initialThreadId` for aui's `useRemoteThreadListRuntime`), before zustand hydration delivers.
 * Local (unsynced) thread ids are never restorable, and a frame asked for a new conversation
 * restores none.
 */
export function readBootThreadId(): string | null {
  if (isNewConversationRequested()) return null;
  const raw = readJsonFromLocalStorage<{ state?: Partial<SyncState> } | null>({
    key: SYNC_STORAGE_KEY,
    fallback: null,
  });
  const threadId = raw?.state?.threadId;
  if (typeof threadId !== 'string' || threadId.length === 0) return null;
  if (isLocalThreadId(threadId)) return null;

  if (safeLocalStorage.getItem('force-thread-restore') === 'true') return threadId;

  const syncTimeout = getSyncTimeout();
  if (!syncTimeout) return null;

  const heartbeat = raw?.state?.heartbeatTimestamp;
  if (typeof heartbeat !== 'number') return null;
  return (Date.now() - heartbeat) / 1000 <= syncTimeout ? threadId : null;
}

export const useSyncStore = create<SyncStore>()(
  persist(
    set => ({
      ...defaultState,

      setSidebar: value =>
        set(state => ({
          sidebar: { ...state.sidebar, ...value },
        })),
      setComposerText: (threadId, text) =>
        set(state => ({
          composerTexts: { ...state.composerTexts, [threadId]: text },
        })),
      clearComposerText: threadId =>
        set(state => {
          const { [threadId]: _, ...rest } = state.composerTexts;
          return { composerTexts: rest };
        }),
      setThreadId: value => set({ threadId: value }),
      setHeartbeatTimestamp: value => set({ heartbeatTimestamp: value }),
    }),
    {
      name: SYNC_STORAGE_KEY,
      storage: createJSONStorage(() => ({
        getItem: (key: string) => safeLocalStorage.getItem(key),
        setItem: (key: string, value: string) => safeLocalStorage.setItem(key, value),
        removeItem: (key: string) => safeLocalStorage.removeItem(key),
      })),
      partialize: state => ({
        sidebar: state.sidebar,
        composerTexts: state.composerTexts,
        threadId: state.threadId,
        heartbeatTimestamp: state.heartbeatTimestamp,
      }),
      merge: (persistedState, currentState) => {
        const syncTimeout = getSyncTimeout();
        const forceRestore = safeLocalStorage.getItem('force-thread-restore') === 'true';

        if (typeof persistedState !== 'object' || persistedState === null) {
          return currentState;
        }

        const typedPersistedState = persistedState as SyncState;

        if (!syncTimeout) {
          if (forceRestore && typedPersistedState.threadId) {
            return { ...currentState, threadId: typedPersistedState.threadId };
          }
          return currentState;
        }

        const timestamp = typedPersistedState.heartbeatTimestamp;

        if (!timestamp) return currentState;

        const ageInSeconds = (Date.now() - timestamp) / 1000;
        if (ageInSeconds > syncTimeout) {
          return { ...currentState, ...defaultState };
        }

        return { ...currentState, ...typedPersistedState };
      },
    },
  ),
);
